import { computed, onMounted, reactive, ref, watch, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useEventBus } from '@vueuse/core'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { FeedbackTopicType } from '@/core/models/feedback-topic'
import { perspectivesFromTopic } from '@/core/models/perspective'
import type { Perspective } from '@/core/models/perspective'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { useSnackStore } from '@/stores/snack.store'
import { EventKey } from '@/core/events/event-key'
import usePut from '@/core/composables/api/usePut'

export type PerspectiveTarget = {
  perspectiveKey: string
  target: number
}

export interface CreateCoachFeedbackGoalFormData {
  feedbackSenderIds: string[]
  feedbackTopicId: string | undefined
  perspectiveCounts: Record<string, number>
}

export type CreateCoachFeedbackGoalErrorKey =
  | keyof Pick<CreateCoachFeedbackGoalFormData, 'feedbackSenderIds' | 'feedbackTopicId'>
  | 'perspectiveCounts'
  | 'noSupportedPerspectives'

export type PutCoachFeedbackGoalPayload = {
  feedbackTopicId: string
  coacheeIds: string[]
  perspectiveTargets: PerspectiveTarget[]
  isActive: boolean
}

export const useCreateCoachFeedbackGoal = (
  isOpen: Ref<boolean>,
  onCreated?: (goalId: string) => void,
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const feedbackTopicStore = useFeedbackTopicStore()
  const { templates, loadingStatus: templateLoadingStatus } = storeToRefs(feedbackTopicStore)
  const { loadTemplates } = feedbackTopicStore

  const goalsUpdatedBus = useEventBus(EventKey.coachFeedbackGoalsUpdated)

  const form = reactive<CreateCoachFeedbackGoalFormData>({
    feedbackSenderIds: [],
    feedbackTopicId: undefined,
    perspectiveCounts: {},
  })

  const errors = ref<Map<CreateCoachFeedbackGoalErrorKey, string>>(new Map())
  const step = ref<0 | 1>(0)
  const {
    mutate,
    status: putStatus,
    data: feedbackGoalId,
  } = usePut<string, PutCoachFeedbackGoalPayload>('coaching/feedback-goals/new')

  const filteredTemplates = computed(() =>
    templates.value.filter(
      (template) =>
        template.type === FeedbackTopicType.Evaluation || template.type === FeedbackTopicType.Pulse,
    ),
  )

  const selectedTopic = computed(() =>
    form.feedbackTopicId
      ? templates.value.find((template) => template.id === form.feedbackTopicId)
      : undefined,
  )

  const goalPerspectives = computed((): Perspective[] => {
    const topic = selectedTopic.value
    if (!topic) {
      return []
    }
    return perspectivesFromTopic(topic)
  })

  const getPerspectiveCount = (p: Perspective): number => form.perspectiveCounts[p.key] ?? 0

  const adjustPerspectiveCount = (p: Perspective, action: 'add' | 'remove') => {
    const current = form.perspectiveCounts[p.key] ?? 0
    if (action === 'add') {
      form.perspectiveCounts[p.key] = current + 1
    } else if (current > 0) {
      form.perspectiveCounts[p.key] = current - 1
    }
    errors.value = new Map()
  }

  const setPerspectiveCount = (p: Perspective, value: number) => {
    form.perspectiveCounts[p.key] = Math.max(0, Math.trunc(Number(value)) || 0)
    errors.value = new Map()
  }

  const resetCountsForTopic = () => {
    const next: Record<string, number> = {}
    for (const p of goalPerspectives.value) {
      next[p.key] = 0
    }
    form.perspectiveCounts = next
  }

  const buildPerspectiveTargets = (): PerspectiveTarget[] =>
    goalPerspectives.value.map((p) => ({
      perspectiveKey: p.key,
      target: form.perspectiveCounts[p.key] ?? 0,
    }))

  const validateStep = () => {
    errors.value = new Map()

    if (step.value === 0) {
      if (form.feedbackSenderIds.length === 0) {
        errors.value.set('feedbackSenderIds', t('feedback.formValidation.members'))
      }
      if (!form.feedbackTopicId) {
        errors.value.set('feedbackTopicId', t('feedback.formValidation.templates'))
      }
    }

    if (step.value === 1) {
      if (goalPerspectives.value.length === 0) {
        errors.value.set(
          'noSupportedPerspectives',
          t('feedback.formValidation.feedbackGoalNoPerspectives'),
        )
        return
      }
      for (const p of goalPerspectives.value) {
        const n = form.perspectiveCounts[p.key]
        if (!Number.isInteger(n) || n < 0) {
          errors.value.set(
            'perspectiveCounts',
            t('feedback.formValidation.feedbackGoalPerspectiveCount'),
          )
          return
        }
      }
    }
  }

  const nextStep = () => {
    validateStep()
    if (errors.value.size > 0) {
      return
    }
    if (step.value === 0) {
      if (goalPerspectives.value.length === 0) {
        errors.value.set(
          'noSupportedPerspectives',
          t('feedback.formValidation.feedbackGoalNoPerspectives'),
        )
        return
      }
      resetCountsForTopic()
      step.value = 1
    }
  }

  const previousStep = () => {
    if (step.value > 0) {
      step.value = 0
    }
    errors.value = new Map()
  }

  const clearForm = () => {
    errors.value = new Map()
    form.feedbackSenderIds = []
    form.feedbackTopicId = undefined
    form.perspectiveCounts = {}
    step.value = 0
  }

  watch(
    () => form.feedbackTopicId,
    () => {
      errors.value = new Map()
    },
  )

  watch(
    () => form.perspectiveCounts,
    () => {
      errors.value = new Map()
    },
    { deep: true },
  )

  watch(isOpen, (open) => {
    if (!open) {
      clearForm()
      return
    }
  })

  onMounted(() => {
    loadTemplates()
  })

  const saveGoal = async () => {
    validateStep()
    if (errors.value.size > 0) {
      return
    }

    const payload: PutCoachFeedbackGoalPayload = {
      feedbackTopicId: form.feedbackTopicId!,
      coacheeIds: form.feedbackSenderIds,
      perspectiveTargets: buildPerspectiveTargets(),
      isActive: true,
    }

    await mutate(payload)

    if (putStatus.value === ApiRequestStatus.Success) {
      goalsUpdatedBus.emit()
      if (onCreated) {
        onCreated(feedbackGoalId.value)
      }
      snackStore.showInfo(t('coaching.feedbackGoals.createSuccess'))
      return
    }

    snackStore.showError(t('coaching.feedbackGoals.createError'))
  }

  const isSaving = computed(() => putStatus.value === ApiRequestStatus.Loading)

  return {
    step,
    nextStep,
    previousStep,
    saveGoal,
    form,
    errors,
    filteredTemplates,
    templateLoadingStatus,
    goalPerspectives,
    getPerspectiveCount,
    adjustPerspectiveCount,
    setPerspectiveCount,
    isSaving,
  }
}
